import {
  Box,
  Button,
  Col,
  type ColumnType,
  ErrorContent,
  Flex,
  HitCounter,
  Loading,
  Row,
  Skeleton,
  Space,
  TablePageSizeSelector,
} from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import {
  useGenerateProjectsCsv,
  useNestedPolicies,
  useProjectSummary,
  useProjects,
} from '@/features/project/_hooks/project';
import {
  useTableColumnsSettingStorage,
  useTableNestSettingStorage,
} from '@/features/project/_hooks/search-projects/local-storage';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import type { TableNestSettingModel } from '@/features/project/_models/project-filter';
import { INITIAL_PAGE } from '@/features/project/_utils/constants';
import {
  DISPLAY_SETTING_KEY,
  FEATURE_TYPE_KEY,
  getStorageSettingKey,
} from '@/features/project/_utils/storage-key';
import {
  FEATURE_KEY,
  FundSheetTableColumnSettingsKeyModifier,
} from '@/features/project/_utils/storage-key';
import { useHandle403ErrorWhenSearching as useHandle403ErrorWhenSearchingForPS } from '@/features/public-site/hooks';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { getInitialValueOfOptionalFields } from '@/features/search/_utils/projects-search';
import { useMessage } from '@/providers';
import { isPS } from '@/utils';
import { fundSheetTypeEnum } from '@/utils/enum';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSortingState } from '../../_components/ProjectTableSorterProvider';
import { getColumnVisibility } from '../../_utils/column-visibility';
import { convertRequestParams } from '../../_utils/convert-request-params';
import { ProjectTableColumnLabelModifier } from '../_models/project-filter';
import type { ProjectListElementModel } from '../_models/projects';
import { DisplaySettingModal } from './DisplaySettingModal';
import { ExpandablePolicies } from './ExpandablePoliciesTable';
import { ProjectSummary } from './ProjectSummary';
import { ProjectsTable } from './ProjectsTable';
import { ProjectsTableSkeleton } from './ProjectsTableSkeleton';

type Project = z.infer<typeof ProjectListElementModel>;
type TableNestSetting = z.infer<typeof TableNestSettingModel>;

type Props = {
  projectTableActionsColumn: ColumnType<Project>;
  hideDisplaySetting?: boolean;
} & (
  | {
      isFromGroupPage: true;
      addAllProjectButton: ReactNode;
      groupFiscalYear: string;
    }
  | {
      isFromGroupPage: false;
    }
);

const SkeletonContents: FC = () => {
  return (
    <Box bordered padding={16} borderRadius={0}>
      <Row gutter={8}>
        <Col>
          <Skeleton.Avatar active size="small" />
        </Col>
        <Col>
          <Skeleton.Input active size="small" />
        </Col>
      </Row>
    </Box>
  );
};

export const FilterResult: FC<Props> = ({
  hideDisplaySetting = false,
  projectTableActionsColumn,
  ...rest
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { searchQueryStates, setSearchQueryStates } =
    useProjectsPageSearchQueryStates();
  const { isLoading } = useProjectsSearchStore();

  const { pageSize, sheetType, fundSheetType } = searchQueryStates;

  const { skipAdministrativeReformTeamEvaluation } =
    getInitialValueOfOptionalFields({
      searchQueryStates,
      initialFiscalYearByGroup: rest.isFromGroupPage
        ? rest.groupFiscalYear
        : undefined,
    });

  const [openDisplaySetting, setOpenDisplaySettingModal] = useState(false);

  const hideProjectSummary = sheetType !== 'RS';

  const me = useMe();

  const tableNestSettingKey = getStorageSettingKey(
    DISPLAY_SETTING_KEY.TABLE_NEST_SETTING,
    FEATURE_KEY[sheetType],
    rest.isFromGroupPage ? FEATURE_TYPE_KEY.GROUP : FEATURE_TYPE_KEY.LIST,
  );

  const tableColumnsSettingKey = getStorageSettingKey(
    DISPLAY_SETTING_KEY.TABLE_COLUMNS_SETTING,
    sheetType === 'KS'
      ? FEATURE_KEY[
          FundSheetTableColumnSettingsKeyModifier[fundSheetType ?? 'standard']
        ]
      : FEATURE_KEY[sheetType],
    rest.isFromGroupPage ? FEATURE_TYPE_KEY.GROUP : FEATURE_TYPE_KEY.LIST,
  );

  const { tableNestSettingStorage, setTableNestSettingStorage } =
    useTableNestSettingStorage(me.data?.id ?? '', tableNestSettingKey);

  const { tableColumnsSettingStorage, setTableColumnsSettingStorage } =
    useTableColumnsSettingStorage(me.data?.id ?? '', tableColumnsSettingKey);

  const [tableNestSetting, setTableNestSetting] = useState<TableNestSetting>(
    tableNestSettingStorage ?? {
      project: true,
      policyLevel1: false,
      policyLevel2: false,
      policyLevel3: false,
    },
  );
  const {
    initiallyVisibleColumns,
    selectableColumns,
    buildValidatedTableColumns,
  } = useMemo(() => {
    return getColumnVisibility({
      sheetType,
      skipAdministrativeReformTeamEvaluation:
        typeof skipAdministrativeReformTeamEvaluation === 'string'
          ? skipAdministrativeReformTeamEvaluation === 'true'
          : null,
      fundSheetType,
    });
  }, [sheetType, skipAdministrativeReformTeamEvaluation, fundSheetType]);

  const [tableColumnsSettings, setTableColumnsSettings] = useState<string[]>(
    buildValidatedTableColumns(
      tableColumnsSettingStorage ?? initiallyVisibleColumns,
    ),
  );

  const hideCsvDownload =
    tableNestSetting.policyLevel1 ||
    tableNestSetting.policyLevel2 ||
    tableNestSetting.policyLevel3 ||
    (sheetType === 'KS' &&
      fundSheetType !== fundSheetTypeEnum.Values.standard) ||
    isPS();

  const displayPolicyLevels = useMemo(
    () =>
      sheetType === 'RS'
        ? [
            ...(tableNestSetting.policyLevel1 ? ['1'] : []),
            ...(tableNestSetting.policyLevel2 ? ['2'] : []),
            ...(tableNestSetting.policyLevel3 ? ['3'] : []),
          ]
        : [],
    [tableNestSetting, sheetType],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    setTableColumnsSettings(
      buildValidatedTableColumns(
        tableColumnsSettingStorage ?? initiallyVisibleColumns,
      ),
    );
  }, [sheetType, fundSheetType]);

  const policyLevelsArr = ['1', '2', '3']; // FIXME: change according dynamic policy level

  const skipLevels = policyLevelsArr.filter((level) =>
    displayPolicyLevels.every((item) => item !== level),
  );
  const { sorting, setSorting } = useSortingState();

  const requestParams = convertRequestParams({
    isDisplayPolicyLevel: !!displayPolicyLevels.length,
    searchQueryStates,
    sorting,
    groupFiscalYear: rest.isFromGroupPage ? rest.groupFiscalYear : undefined,
    ...rest,
  });

  const policyData = useNestedPolicies({
    params: {
      ...requestParams,
      page: undefined,
      ordering: undefined,
      skipLevel: skipLevels.join(','),
    },
    enabled: !!displayPolicyLevels.length,
  });

  const projectsData = useProjects({
    enabled: !!requestParams.pageSize,
    params: {
      ...requestParams,
      page: INITIAL_PAGE,
    },
  });

  useHandle403ErrorWhenSearchingForPS(projectsData);

  const projectsSummaryData = useProjectSummary({
    enabled: !!requestParams.pageSize,
    params: {
      ...requestParams,
      page: INITIAL_PAGE,
    },
  });

  const { mutateAsync } = useGenerateProjectsCsv();

  if (policyData.isError || projectsSummaryData.isError) {
    return <ErrorContent reloadable={false} />;
  }

  if (isLoading) {
    return <ProjectsTableSkeleton />;
  }

  if (displayPolicyLevels.length && policyData.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Flex gap={16} vertical>
        {!rest.isFromGroupPage &&
        projectsSummaryData.data?.summary &&
        !hideProjectSummary ? (
          <Box width="100%">
            <ProjectSummary
              aggregationSummary={projectsSummaryData.data.summary}
            />
          </Box>
        ) : (
          <>{isPS() && !hideProjectSummary ? <SkeletonContents /> : null}</>
        )}
        <Flex justify="space-between" align="center">
          <Space>
            <HitCounter
              count={
                (displayPolicyLevels.length
                  ? policyData.data?.projectCount
                  : projectsData.data?.count) ?? 0
              }
              label={t('relevant')}
            />
            {rest.isFromGroupPage && rest.addAllProjectButton}
          </Space>

          <Space>
            <TablePageSizeSelector
              defaultPageSize={Number(pageSize)}
              onChange={(pageSize) => {
                setSearchQueryStates((pre) => ({ ...pre, page: 1, pageSize }));
              }}
            />
            {!hideDisplaySetting && (
              <Button
                onClick={() => {
                  setOpenDisplaySettingModal(true);
                }}
              >
                {t('summary-display-setting')}
              </Button>
            )}
            {!hideCsvDownload && (
              <Button
                onClick={async () => {
                  try {
                    await mutateAsync({
                      query: {
                        searchQuery: requestParams,
                        page: INITIAL_PAGE,
                      },
                    });
                    await openMessage(
                      'success',
                      t('export-file-requested', {
                        target: t('project-list-csv-export'),
                      }),
                    );
                  } catch {
                    await openMessage('error', t('error-message-csv-download'));
                  }
                }}
              >
                {t('csv-download')}
              </Button>
            )}
          </Space>
        </Flex>
        <Flex>
          {displayPolicyLevels.length ? (
            <ExpandablePolicies
              requestParams={requestParams}
              setSorting={setSorting}
              pageSize={pageSize}
              rootPolicyLevel={
                displayPolicyLevels.length > 0
                  ? Number(displayPolicyLevels[0])
                  : undefined
              }
              data={policyData.data?.result ?? []}
              projectTableColumns={tableColumnsSettings}
              projectTableActionsColumn={projectTableActionsColumn}
              {...rest}
            />
          ) : (
            <ProjectsTable
              requestParams={requestParams}
              pageSize={pageSize}
              setSorting={setSorting}
              columnsSettings={tableColumnsSettings}
              tableActionsColumn={projectTableActionsColumn}
              {...rest}
            />
          )}
        </Flex>
      </Flex>

      <DisplaySettingModal
        key={JSON.stringify(tableColumnsSettings) + sheetType}
        open={openDisplaySetting}
        tableColumnsSettings={tableColumnsSettings.filter(
          (col) => col !== 'name',
        )}
        title={t('summary-display-setting')}
        onSubmit={async ({
          tableNestSetting: newTableNestSetting,
          tableColumnsSettings: newTableColumnsSettings,
        }) => {
          const tableColumnsSetting = buildValidatedTableColumns(
            newTableColumnsSettings,
          );
          setTableNestSetting((prev) => ({
            ...prev,
            ...newTableNestSetting,
          }));
          setTableColumnsSettings(tableColumnsSetting);
          setOpenDisplaySettingModal(false);

          setTableNestSettingStorage(newTableNestSetting);
          // ストレージには固定列を除いた列のみを保存
          setTableColumnsSettingStorage(
            newTableColumnsSettings.filter((col) => col !== 'name'),
          );
        }}
        getColumnTitle={(key) => ProjectTableColumnLabelModifier[key] ?? ''}
        tableNestSetting={{
          project: tableNestSetting.project,
          ...(sheetType === 'RS' && {
            policyLevel1: tableNestSetting.policyLevel1,
            policyLevel2: tableNestSetting.policyLevel2,
            policyLevel3: tableNestSetting.policyLevel3,
          }),
        }}
        tableNestSettingOptions={
          sheetType === 'RS'
            ? ['project', 'policyLevel1', 'policyLevel2', 'policyLevel3']
            : ['project']
        }
        allColumns={selectableColumns}
        onCancel={() => {
          setOpenDisplaySettingModal(false);
        }}
      />
    </>
  );
};
